import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Rocket, GitBranch } from 'lucide-react';
import { APP_VERSION } from '@uniticket/shared';
import { Changelog } from './Changelog';
import { Tutorial } from './Tutorial';
import { Rich } from './rich-text';

// "Documentation" tab. First doc: how a "fake ODF" (dummy ODF) works, based on the source Fiber platform.
// The long-form content is kept as a bilingual constant (EN/FR) rather than dozens of i18n keys — it still
// follows the app's EN|FR toggle because useTranslation re-renders the page on languageChanged, and we pick
// the active language from i18n.resolvedLanguage. Extend CONTENT (or add sections) to grow the docs.

type Lang = 'en' | 'fr';

interface DiagramLabels {
  pop: string; popSub: string; casNormal: string; fakeBranch: string;
  rackReal: string; rackRealSub: string; panel: string; panelSub: string; port: string; portSub: string;
  rackDummy: string; rackDummySub: string; panelDummy: string; panelDummySub: string; portDummy: string; portDummySub: string;
  addressFiber: string; addressFiberSub: string; address: string; fakeOdfLabel: string; normalPort: string;
}
interface Step { t: string; s: string; svc: string; }
interface DocContent {
  title: string; subtitle: string; lead: string;
  s1h: string; s1p1: string; s1p2: string; s1li0: string; s1li999: string; s1p3: string;
  s2h: string; s2items: string[];
  s3h: string; s3cap: string;
  s4h: string; s4p: string; s4steps: Step[]; s4cap: string;
  s5h: string; s5items: string[];
  s6h: string; s6p1: string; s6p2: string;
  s7h: string; s7p1: string; s7colTable: string; s7colWhat: string; s7rows: [string, string][]; s7p2: string; s7p3: string;
  ref: string;
  diagram: DiagramLabels;
}

const CONTENT: Record<Lang, DocContent> = {
  fr: {
    title: 'Le « fake ODF », expliqué',
    subtitle: "Comment un ODF fictif (dummy) est créé pour une adresse — plateforme Fiber (source réelle)",
    lead: "**En une phrase :** un « fake ODF » est un **emplacement factice** dans un central (POP) auquel on rattache la fibre d'une adresse **quand le vrai emplacement physique n'est pas encore connu** — pour que l'adresse puisse exister et être commandée sans attendre le brassage sur le terrain.",
    s1h: '1. C’est quoi un ODF, et un « fake » ODF ?',
    s1p1: "Dans un central **(Point of Presence — POP)**, chaque fibre client aboutit à un point physique très précis : un **rack** → un **panneau de brassage** (patch panel) → un **port**. C'est cet emplacement qu'on appelle l'**ODF**.",
    s1p2: "Un **fake ODF** (ODF fictif / « dummy ») est un rack + panneau + port **spéciaux**, reconnaissables à leur étiquette :",
    s1li0: '`0` = « connecté mais emplacement inconnu » — un fourre-tout, **un par POP**.',
    s1li999: '`999` = « pas encore connecté » — temporaire pendant la construction, **partageable par plusieurs adresses**.',
    s1p3: "On rattache la fibre de l'adresse à ce port factice en attendant de connaître le vrai port.",
    s2h: '2. Pourquoi ça existe ?',
    s2items: [
      "**Enregistrer / commander une adresse avant le brassage physique.** Une adresse en cours de construction (statut `HOMES_CONNECT`) peut démarrer avec un ODF fictif ; le vrai ODF le remplacera plus tard.",
      "**Ne jamais perdre l'historique.** Quand un port est réattribué à une autre fibre, l'ancienne fibre ne peut pas être supprimée (audit, commandes). On la « gare » alors sur le port dummy (réf. FTTH-1186).",
      "**Souplesse pendant le chantier.** Le port `999` peut être partagé par un nombre illimité d'adresses ; le port `0` sert de fourre-tout par POP.",
    ],
    s3h: '3. Le modèle de données',
    s3cap: "Un « fake ODF » = simplement la branche dummy (orange) de cette même hiérarchie. La fibre de l'adresse pointe vers le port dummy au lieu d'un vrai port.",
    s4h: '4. Comment un fake ODF est créé',
    s4p: "La création suit une logique **« trouver ou créer »** (find-or-create) : si l'élément dummy existe déjà pour ce POP, on le réutilise ; sinon on le crée. En 4 temps :",
    s4steps: [
      { t: 'Trouver / créer le RACK dummy', s: 'label 0', svc: 'RackService' },
      { t: 'Trouver / créer le PATCH PANEL dummy', s: 'label 0 · hauteur 0 · aucun port réel', svc: 'PatchPanelService' },
      { t: 'Trouver / créer le PORT dummy', s: 'position 0 · statut INACTIVE', svc: 'PatchPanelService' },
      { t: "Rattacher la FIBRE de l'adresse à ce port dummy", s: "l'ancien port (s'il existait) repasse INACTIVE ; on enregistre la fibre", svc: 'AddressFiberService' },
    ],
    s4cap: 'Les étapes 1→3 sont enchaînées par PopService (qui renvoie directement le port dummy du POP), puis l’étape 4 y branche la fibre.',
    s5h: '5. Quand est-ce déclenché ?',
    s5items: [
      "**Conflit de port à l'import du chantier** — si le port indiqué est déjà pris : l'ancienne fibre est déplacée sur le dummy, la nouvelle prend le vrai port.",
      "**Correction terrain** (portail Delivery Feedback) — un technicien corrige le port : l'ancienne fibre part sur le dummy, la fibre corrigée prend le nouveau port.",
      "**Création d'adresse** via le formulaire — une fibre n'est créée que si un port est choisi.",
    ],
    s6h: '6. Et après ? Le vrai ODF remplace le fake',
    s6p1: "Le fake ODF est **temporaire**. Dès que le vrai emplacement est connu (import chantier ou correction terrain), la fibre est rebranchée sur le **vrai port** et l'ancien repasse INACTIVE.",
    s6p2: "**Garde-fou :** tant que la fibre pointe encore sur un rack dummy (`0` / `999`), la validation impose que le splitter soit sur le rack virtuel « 201 » — c'est ce qui empêche de commander sur un emplacement pas encore réel (VirtualRackService).",
    s7h: '7. Où est-ce écrit ? (la persistance)',
    s7p1: "Tout est écrit dans la **base de données du DMP (PostgreSQL)** — **pas** dans un fichier Excel/tableau, ni « sur la PC ». Et il n'y a **pas de table spéciale « fake ODF »** : ce sont **les mêmes tables que les vrais ODF**, reconnaissables aux labels `0` / `999`.",
    s7colTable: 'Table (DMP)', s7colWhat: 'Ce qui est écrit pour un fake ODF',
    s7rows: [
      ['rack', "1 ligne label = '0' (créée si absente)"],
      ['patch_panel', "1 ligne label = '0', hauteur 0"],
      ['patch_panel_port', 'logical_position = 0, statut INACTIVE'],
      ['address_fiber', "1 ligne reliant address_id ↔ patch_panel_port_id (le port dummy) — c'est CETTE ligne qui « fait apparaître » le fake ODF pour l'adresse"],
    ],
    s7p2: "**Quand l'écriture devient effective :** le code appelle `persist()` (prépare en mémoire) puis `flush()` (écrit réellement en base, en une transaction). C'est le traitement qui orchestre (import chantier, contrôleur de création, portail de correction) qui déclenche le flush. (`save()` = persist + flush immédiat.)",
    s7p3: "Pour « voir » un fake ODF en base : dans `address_fiber`, suivre le `patch_panel_port_id` de la fibre → si le `rack` a le label `0` ou `999`, c'est un fake ODF.",
    ref: "Réf. code (source réelle) : src/Pop/Entity/{PointOfPresence,Rack,PatchPanel,PatchPanelPort,AddressFiber,Splitter}.php · src/Pop/Service/{RackService,PatchPanelService,PopService,AddressFiberService}.php · labels 0/999 : src/Pop/Type/SpecialRackPatchPortLabels.php · validation : src/Address/Service/VirtualRackService.php.",
    diagram: {
      pop: 'Point of Presence', popSub: 'le central (POP)', casNormal: 'CAS NORMAL', fakeBranch: 'LE « FAKE ODF » (branche dummy)',
      rackReal: 'Rack réel', rackRealSub: 'label 101 / 103…', panel: 'Patch Panel', panelSub: 'label = hauteur', port: 'Port', portSub: 'position 1–192 · ACTIVE',
      rackDummy: 'Rack DUMMY', rackDummySub: 'label 0 ou 999', panelDummy: 'Patch Panel dummy', panelDummySub: 'label 0 · hauteur 0', portDummy: 'Port dummy', portDummySub: 'position 0 · INACTIVE',
      addressFiber: 'Address Fiber', addressFiberSub: "la fibre de l'adresse", address: 'Adresse', fakeOdfLabel: '= le fake ODF', normalPort: '(cas normal : vrai port)',
    },
  },
  en: {
    title: 'The “fake ODF”, explained',
    subtitle: 'How a dummy ODF is created for an address — Fiber platform (real source)',
    lead: "**In one sentence:** a “fake ODF” is a **placeholder location** in a hub (POP) that an address's fiber is attached to **when the real physical location isn't known yet** — so the address can exist and be ordered without waiting for the field patching.",
    s1h: '1. What is an ODF, and a “fake” ODF?',
    s1p1: "In a hub **(Point of Presence — POP)**, every customer fiber ends at a very precise physical point: a **rack** → a **patch panel** → a **port**. That location is the **ODF**.",
    s1p2: 'A **fake ODF** (dummy ODF) is a **special** rack + panel + port, recognisable by their label:',
    s1li0: '`0` = “connected but location unknown” — a catch-all, **one per POP**.',
    s1li999: '`999` = “not yet connected” — temporary during construction, **shareable by several addresses**.',
    s1p3: "The address's fiber is attached to this placeholder port until the real port is known.",
    s2h: '2. Why does it exist?',
    s2items: [
      '**Register / order an address before the physical patching.** An address under construction (status `HOMES_CONNECT`) can start with a fake ODF; the real ODF replaces it later.',
      '**Never lose history.** When a port is reassigned to another fiber, the old fiber cannot be deleted (audit, orders). It is “parked” on the dummy port instead (ref. FTTH-1186).',
      '**Flexibility during construction.** The `999` port can be shared by unlimited addresses; the `0` port is a per-POP catch-all.',
    ],
    s3h: '3. The data model',
    s3cap: "A “fake ODF” = simply the dummy branch (orange) of this same hierarchy. The address's fiber points to the dummy port instead of a real port.",
    s4h: '4. How a fake ODF is created',
    s4p: "Creation follows a **find-or-create** logic: if the dummy element already exists for this POP it is reused, otherwise it is created. In 4 steps:",
    s4steps: [
      { t: 'Find / create the dummy RACK', s: 'label 0', svc: 'RackService' },
      { t: 'Find / create the dummy PATCH PANEL', s: 'label 0 · height 0 · no real port', svc: 'PatchPanelService' },
      { t: 'Find / create the dummy PORT', s: 'position 0 · status INACTIVE', svc: 'PatchPanelService' },
      { t: "Attach the address's FIBER to that dummy port", s: 'the old port (if any) goes back to INACTIVE; the fiber is saved', svc: 'AddressFiberService' },
    ],
    s4cap: 'Steps 1→3 are chained by PopService (which returns the POP’s dummy port directly), then step 4 attaches the fiber.',
    s5h: '5. When is it triggered?',
    s5items: [
      '**Port conflict on the construction import** — if the given port is already taken: the old fiber is moved to the dummy, the new one takes the real port.',
      '**Field correction** (Delivery Feedback portal) — a technician corrects the port: the old fiber goes to the dummy, the corrected fiber takes the new port.',
      '**Address creation** via the form — a fiber is created only if a port is selected.',
    ],
    s6h: '6. And then? The real ODF replaces the fake',
    s6p1: 'The fake ODF is **temporary**. As soon as the real location is known (construction import or field correction), the fiber is re-attached to the **real port** and the old one goes back to INACTIVE.',
    s6p2: '**Guard-rail:** as long as the fiber still points to a dummy rack (`0` / `999`), validation requires the splitter to be on the virtual rack “201” — which prevents ordering on a not-yet-real location (VirtualRackService).',
    s7h: '7. Where is it written? (persistence)',
    s7p1: "Everything is written to the **DMP database (PostgreSQL)** — **not** to an Excel/spreadsheet, nor “on the PC”. And there is **no special “fake ODF” table**: it is **the same tables as real ODFs**, recognisable by the labels `0` / `999`.",
    s7colTable: 'Table (DMP)', s7colWhat: 'What is written for a fake ODF',
    s7rows: [
      ['rack', "1 row label = '0' (created if missing)"],
      ['patch_panel', "1 row label = '0', height 0"],
      ['patch_panel_port', 'logical_position = 0, status INACTIVE'],
      ['address_fiber', "1 row linking address_id ↔ patch_panel_port_id (the dummy port) — THIS row is what makes the fake ODF “appear” for the address"],
    ],
    s7p2: "**When the write becomes effective:** the code calls `persist()` (prepares in memory) then `flush()` (actually writes to the DB, in a transaction). The orchestrating process (construction import, create controller, correction portal) triggers the flush. (`save()` = persist + flush at once.)",
    s7p3: "To “see” a fake ODF in the DB: in `address_fiber`, follow the fiber's `patch_panel_port_id` → if the `rack` has label `0` or `999`, it is a fake ODF.",
    ref: 'Code reference (real source): src/Pop/Entity/{PointOfPresence,Rack,PatchPanel,PatchPanelPort,AddressFiber,Splitter}.php · src/Pop/Service/{RackService,PatchPanelService,PopService,AddressFiberService}.php · labels 0/999: src/Pop/Type/SpecialRackPatchPortLabels.php · validation: src/Address/Service/VirtualRackService.php.',
    diagram: {
      pop: 'Point of Presence', popSub: 'the hub (POP)', casNormal: 'NORMAL CASE', fakeBranch: 'THE “FAKE ODF” (dummy branch)',
      rackReal: 'Real rack', rackRealSub: 'label 101 / 103…', panel: 'Patch Panel', panelSub: 'label = height', port: 'Port', portSub: 'position 1–192 · ACTIVE',
      rackDummy: 'DUMMY rack', rackDummySub: 'label 0 or 999', panelDummy: 'Dummy patch panel', panelDummySub: 'label 0 · height 0', portDummy: 'Dummy port', portDummySub: 'position 0 · INACTIVE',
      addressFiber: 'Address Fiber', addressFiberSub: "the address's fiber", address: 'Address', fakeOdfLabel: '= the fake ODF', normalPort: '(normal case: real port)',
    },
  },
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-6">
      <h2 className="text-[15px] font-extrabold text-brand-rose border-b-2 border-brand-rose/20 pb-1">{title}</h2>
      <div className="mt-2 space-y-2 text-[13.5px] leading-relaxed text-slate-700">{children}</div>
    </section>
  );
}

// ── Diagram 1: the data model (POP → rack → panel → port → fiber → address; dummy branch highlighted) ──
function DataModelDiagram({ d }: { d: DiagramLabels }) {
  const B = ({ x, y, w, title, sub, dummy, ok }: { x: number; y: number; w: number; title: string; sub?: string; dummy?: boolean; ok?: boolean }) => (
    <g>
      <rect x={x} y={y} width={w} height={44} rx={8}
        fill={dummy ? '#fff7ed' : ok ? '#f0fdf4' : '#fff'} stroke={dummy ? '#f59e0b' : ok ? '#86efac' : '#cbd5e1'} strokeWidth={dummy ? 1.8 : 1.5} />
      <text x={x + w / 2} y={sub ? y + 19 : y + 27} textAnchor="middle" fontSize="12" fontWeight={700} fill={dummy ? '#9a3412' : ok ? '#166534' : '#1f2937'}>{title}</text>
      {sub && <text x={x + w / 2} y={y + 34} textAnchor="middle" fontSize="10" fill="#64748b">{sub}</text>}
    </g>
  );
  return (
    <svg viewBox="0 0 720 470" className="w-full h-auto" fontFamily="inherit">
      <defs>
        <marker id="dm-arr" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto"><path d="M0,0 L7,3 L0,6 Z" fill="#94a3b8" /></marker>
        <marker id="dm-arrA" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto"><path d="M0,0 L7,3 L0,6 Z" fill="#ea7317" /></marker>
      </defs>
      <B x={280} y={8} w={160} title={d.pop} sub={d.popSub} />
      <text x={185} y={82} textAnchor="middle" fontSize="10" fontWeight={700} fill="#334155">{d.casNormal}</text>
      <text x={545} y={82} textAnchor="middle" fontSize="10.5" fontWeight={700} fill="#ea7317">{d.fakeBranch}</text>
      <B x={85} y={90} w={200} title={d.rackReal} sub={d.rackRealSub} />
      <B x={85} y={170} w={200} title={d.panel} sub={d.panelSub} />
      <B x={85} y={250} w={200} title={d.port} sub={d.portSub} />
      <B x={435} y={90} w={220} title={d.rackDummy} sub={d.rackDummySub} dummy />
      <B x={435} y={170} w={220} title={d.panelDummy} sub={d.panelDummySub} dummy />
      <B x={435} y={250} w={220} title={d.portDummy} sub={d.portDummySub} dummy />
      <path d="M330,50 C300,70 220,72 185,88" fill="none" stroke="#94a3b8" strokeWidth={1.5} markerEnd="url(#dm-arr)" />
      <path d="M405,50 C460,70 520,72 545,88" fill="none" stroke="#ea7317" strokeWidth={2} markerEnd="url(#dm-arrA)" />
      <path d="M185,134 L185,168" fill="none" stroke="#94a3b8" strokeWidth={1.5} markerEnd="url(#dm-arr)" />
      <path d="M185,214 L185,248" fill="none" stroke="#94a3b8" strokeWidth={1.5} markerEnd="url(#dm-arr)" />
      <path d="M545,134 L545,168" fill="none" stroke="#ea7317" strokeWidth={2} markerEnd="url(#dm-arrA)" />
      <path d="M545,214 L545,248" fill="none" stroke="#ea7317" strokeWidth={2} markerEnd="url(#dm-arrA)" />
      <B x={255} y={378} w={200} title={d.addressFiber} sub={d.addressFiberSub} />
      <B x={500} y={378} w={150} title={d.address} ok />
      <path d="M545,294 C545,340 430,340 380,376" fill="none" stroke="#ea7317" strokeWidth={2} markerEnd="url(#dm-arrA)" />
      <text x={470} y={332} textAnchor="middle" fontSize="10.5" fontWeight={700} fill="#ea7317">{d.fakeOdfLabel}</text>
      <path d="M185,294 C185,345 300,345 320,376" fill="none" stroke="#cbd5e1" strokeWidth={1.5} strokeDasharray="5 4" markerEnd="url(#dm-arr)" />
      <text x={235} y={362} textAnchor="middle" fontSize="10" fill="#64748b">{d.normalPort}</text>
      <path d="M455,401 L498,401" fill="none" stroke="#94a3b8" strokeWidth={1.5} markerEnd="url(#dm-arr)" />
    </svg>
  );
}

// ── Diagram 2: the creation flow (4 find-or-create steps) ──
function CreationFlowDiagram({ steps }: { steps: Step[] }) {
  return (
    <svg viewBox="0 0 720 320" className="w-full h-auto" fontFamily="inherit">
      <defs><marker id="fl-arr" markerWidth="10" markerHeight="10" refX="7" refY="3" orient="auto"><path d="M0,0 L7,3 L0,6 Z" fill="#e5147d" /></marker></defs>
      {steps.map((st, i) => {
        const y = 8 + i * 76;
        const last = i === steps.length - 1;
        return (
          <g key={i}>
            {i > 0 && <path d={`M295,${y - 12} L295,${y}`} fill="none" stroke="#e5147d" strokeWidth={2} markerEnd="url(#fl-arr)" />}
            <rect x={60} y={y} width={470} height={56} rx={9} fill={last ? '#fdf2f8' : '#fff'} stroke="#e5147d" strokeWidth={1.6} />
            <circle cx={88} cy={y + 28} r={15} fill="#e5147d" />
            <text x={88} y={y + 33} textAnchor="middle" fontSize="14" fontWeight={800} fill="#fff">{i + 1}</text>
            <text x={118} y={y + 24} fontSize="12.5" fontWeight={600} fill="#1f2937">{st.t}</text>
            <text x={118} y={y + 42} fontSize="10.5" fill="#64748b">{st.s}</text>
            <text x={660} y={y + 32} textAnchor="end" fontSize="10.5" fontWeight={700} fill="#be1256">{st.svc}</text>
          </g>
        );
      })}
    </svg>
  );
}

export function Documentation() {
  const { i18n } = useTranslation();
  const lang: Lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').startsWith('fr') ? 'fr' : 'en';
  const t = CONTENT[lang];
  // « Nouveautés » d'abord (demande user 2026-08-31) : on ouvre cet onglet pour savoir ce qui vient de
  // changer bien plus souvent que pour relire l'explication du fake ODF, qui elle ne bouge pas.
  // Le « Tutoriel » (demande user du 22-09-2026) vit ICI et pas dans un onglet de la barre latérale :
  // c'est de la documentation, et on l'ouvre pour la même raison qu'on ouvre les deux autres.
  const [tab, setTab] = useState<'changelog' | 'fakeodf' | 'tutorial'>('changelog');
  const isLog = tab === 'changelog';
  const isTuto = tab === 'tutorial';

  return (
    // Le Tutoriel prend plus de largeur (N32.6) : un rail des phases ET une carte côte à côte ne tiennent pas dans
    // la colonne de lecture des deux autres onglets.
    <div className={`${isTuto ? 'max-w-6xl' : 'max-w-4xl'} mx-auto`}>
      {/* Hero — il suit l'onglet actif plutôt que d'annoncer un seul document. */}
      <div className="rounded-2xl bg-brand-gradient text-white px-6 py-5 flex items-center gap-3">
        {isLog ? <Rocket className="w-7 h-7 shrink-0" />
          : isTuto ? <GitBranch className="w-7 h-7 shrink-0" />
          : <BookOpen className="w-7 h-7 shrink-0" />}
        <div>
          <h1 className="text-xl font-extrabold">
            {isLog ? (lang === 'fr' ? 'Nouveautés' : 'What’s new')
              : isTuto ? (lang === 'fr' ? 'Tutoriel' : 'Tutorial')
              : t.title}
          </h1>
          <p className="text-sm opacity-90">
            {isLog
              ? (lang === 'fr'
                  ? `Historique des versions — l’application tourne en ${APP_VERSION}`
                  : `Version history — the app is running ${APP_VERSION}`)
              : isTuto
                ? (lang === 'fr'
                    ? 'Parcours guidé — traiter un ticket, une étape à la fois'
                    : 'Guided path — handle a ticket, one step at a time')
                : t.subtitle}
          </p>
        </div>
      </div>

      <div className="mt-4 flex gap-1 border-b border-slate-200">
        {([
          ['changelog', lang === 'fr' ? 'Nouveautés' : 'What’s new'],
          ['tutorial', lang === 'fr' ? 'Tutoriel' : 'Tutorial'],
          ['fakeodf', lang === 'fr' ? 'Le « fake ODF »' : 'The “fake ODF”'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition-colors ${
              tab === key
                ? 'border-brand-rose text-brand-rose'
                : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {isLog ? <div className="mt-4"><Changelog /></div> : isTuto ? <Tutorial /> : (<>

      <div className="mt-4 rounded-xl border border-brand-rose/20 bg-brand-rose/[0.04] px-4 py-3 text-[13.5px] text-slate-700">
        <Rich text={t.lead} />
      </div>

      <Section title={t.s1h}>
        <p><Rich text={t.s1p1} /></p>
        <p><Rich text={t.s1p2} /></p>
        <ul className="list-disc pl-5 space-y-1">
          <li><Rich text={t.s1li0} /></li>
          <li><Rich text={t.s1li999} /></li>
        </ul>
        <p><Rich text={t.s1p3} /></p>
      </Section>

      <Section title={t.s2h}>
        <ul className="list-disc pl-5 space-y-1.5">
          {t.s2items.map((it, i) => <li key={i}><Rich text={it} /></li>)}
        </ul>
      </Section>

      <Section title={t.s3h}>
        <div className="rounded-xl border border-slate-200 bg-white p-3"><DataModelDiagram d={t.diagram} /></div>
        <p className="text-xs text-slate-500 text-center"><Rich text={t.s3cap} /></p>
      </Section>

      <Section title={t.s4h}>
        <p><Rich text={t.s4p} /></p>
        <div className="rounded-xl border border-slate-200 bg-white p-3"><CreationFlowDiagram steps={t.s4steps} /></div>
        <p className="text-xs text-slate-500 text-center"><Rich text={t.s4cap} /></p>
      </Section>

      <Section title={t.s5h}>
        <ul className="list-disc pl-5 space-y-1.5">
          {t.s5items.map((it, i) => <li key={i}><Rich text={it} /></li>)}
        </ul>
      </Section>

      <Section title={t.s6h}>
        <p><Rich text={t.s6p1} /></p>
        <p><Rich text={t.s6p2} /></p>
      </Section>

      <Section title={t.s7h}>
        <p><Rich text={t.s7p1} /></p>
        <div className="overflow-hidden rounded-xl border border-slate-200">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="bg-brand-rose/[0.06] text-left text-brand-rose">
                <th className="px-3 py-2 font-bold w-44">{t.s7colTable}</th>
                <th className="px-3 py-2 font-bold">{t.s7colWhat}</th>
              </tr>
            </thead>
            <tbody>
              {t.s7rows.map((row, i) => {
                const highlight = row[0] === 'address_fiber';
                return (
                  <tr key={i} className={highlight ? 'bg-amber-50' : ''}>
                    <td className="px-3 py-2 border-t border-slate-100 font-mono text-slate-700 align-top">{row[0]}</td>
                    <td className="px-3 py-2 border-t border-slate-100 text-slate-700">{row[1]}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p><Rich text={t.s7p2} /></p>
        <p><Rich text={t.s7p3} /></p>
      </Section>

      <p className="mt-6 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
        {t.ref}
      </p>
      </>)}
    </div>
  );
}
